import React, {useEffect, useRef, useState} from "react";
import {
  Box,
  Button,
  IconButton,
  Input,
  Typography,
  useTheme,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import {UploadFile} from "ts/api/chat";
import CloseIcon from "@material-ui/icons/Close";
import {v4 as uuidv4} from "uuid";
import clsx from "clsx";
import {cloneDeep} from "lodash";
import messagingService from "api/messaging";
import FlexBox from "view/components/Layout/FlexBox";
import {
  compressImage,
  IMAGE_COMPRESSION_MAX_SIZE_MB,
} from "tools/file/compressImage";
import {useLocalizedNameTranslate} from "hooks/useLocalizedNameTranslate";
import Files from "../Files";
import {useStyles} from "./style";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
type InputBarProps = {
  showId: string;
  standId: string;
  editText: string;
  replayText: string;
  userRole: string;
  onSend: (message: string, files: UploadFile[]) => void;
  onReplayEditClose: () => void;
};

const SendMessage = ({
  showId,
  standId,
  editText,
  replayText,
  userRole,
  onReplayEditClose,
  onSend,
}: InputBarProps) => {
  const [message, setMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<UploadFile[]>([]);
  const [sendError, setSendError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const classes = useStyles();
  const theme = useTheme();
  const t = useLocalizedNameTranslate();

  const deleteFile = async (fileId: string, fileName: string) => {
    await messagingService.deleteFileNotLoaded(
      showId,
      standId,
      fileId,
      fileName
    );
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((currFile) => currFile.id !== fileId)
    );
  };

  const handleUpload = async (newFiles: File[]) => {
    for (const rawFile of newFiles) {
      // eslint-disable-next-line no-await-in-loop
      const file = await compressImage(
        rawFile,
        IMAGE_COMPRESSION_MAX_SIZE_MB.messagingBoard
      );

      const fileId = uuidv4();
      setSelectedFiles((prevFiles: UploadFile[]) => [
        ...prevFiles,
        {file, id: fileId, progress: 0, name: file.name, loading: true},
      ]);

      const uploadTask = messagingService.uploadFile(
        file,
        showId,
        standId,
        fileId
      );
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

          setSelectedFiles((prevFiles) =>
            prevFiles.map((currFile) =>
              currFile.id === fileId ? {...currFile, progress} : currFile
            )
          );
        },
        (error) => {
          console.error("Upload error:", error);
          setSelectedFiles((prevFiles) =>
            prevFiles.map((currFile) =>
              currFile.id === fileId
                ? {...currFile, loading: false, error: true}
                : currFile
            )
          );
        },
        async () => {
          const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
          setSelectedFiles((prevFiles) =>
            prevFiles.map((currFile) =>
              currFile.id === fileId
                ? {...currFile, loading: false, url: downloadURL}
                : currFile
            )
          );
        }
      );
    }
  };
  const onResend = async (fileId: string, fileName: string) => {
    const newFiles = cloneDeep(
      selectedFiles
        .filter((file) => file.id === fileId)
        .map((file) => file.file)
    );
    await deleteFile(fileId, fileName);
    handleUpload(newFiles);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    const newFiles = files.filter((file) => file.size <= MAX_FILE_SIZE);
    handleUpload(newFiles);
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const files: File[] = Array.from(event.dataTransfer.files);
    const newFiles = files.filter((file: File) => file.size <= MAX_FILE_SIZE);

    handleUpload(newFiles);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
  };

  useEffect(() => {
    if (editText) {
      setMessage(editText);
    }
  }, [editText]);
  const sendDisabled =
    !message?.trim() &&
    (selectedFiles.length === 0 || selectedFiles.some((file) => file.loading));
  const handleSend = async () => {
    if (navigator.onLine) {
      if (!sendDisabled) {
        await onSend(message, selectedFiles);
        setMessage("");
        setSelectedFiles([]);
        setSendError(false);

        if (fileInputRef?.current) {
          fileInputRef.current.value = "";
        }
      }
    } else {
      setSendError(true);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (event.shiftKey) {
        setMessage((prevMessage) => {
          const cursorPosition = inputRef?.current?.selectionStart;
          if (cursorPosition) {
            const textBeforeCursor = prevMessage.slice(0, cursorPosition);
            const textAfterCursor = prevMessage.slice(cursorPosition);
            return `${textBeforeCursor}\n${textAfterCursor}`;
          }
          return prevMessage;
        });
      } else {
        handleSend();
      }
    }
  };

  return (
    <FlexBox
      alignItems="flex-start"
      width="100%"
      minHeight="80px"
      justifyContent="space-between"
      flexDirection="column"
    >
      <FlexBox
        alignItems="flex-end"
        width="100%"
        minHeight="80px"
        justifyContent="space-between"
      >
        <FlexBox
          flexDirection="column"
          alignItems="flex-end"
          justifyContent="space-between"
          className={clsx(classes.replayContainer, {
            [classes.sendError]: sendError,
          })}
        >
          {(editText || replayText) && (
            <FlexBox alignCenter className={classes.replayBody}>
              <Box className={classes.marker} />
              <Typography noWrap className={classes.replayText}>
                {editText || replayText}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  editText && setMessage("");
                  onReplayEditClose();
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </FlexBox>
          )}
          {selectedFiles.length > 0 && (
            <FlexBox width="100%">
              <Files
                files={selectedFiles}
                deleteFile={(fileId: string, fileName: string) => {
                  if (fileInputRef?.current) {
                    fileInputRef.current.value = "";
                  }
                  deleteFile(fileId, fileName);
                }}
                onResend={onResend}
              />
            </FlexBox>
          )}
          <Box display="flex" flexDirection="row" width="100%">
            <Input
              multiline
              value={message}
              onChange={handleInputChange}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleFileDrop}
              onKeyDown={handleKeyPress}
              placeholder={
                t
                  ? t("exhibitor.chat.message_placeholder", "Message...")
                  : "Message..."
              }
              inputRef={inputRef}
              disableUnderline
              fullWidth
              className={classes.input}
              id="chat-text-input"
            />
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              style={{display: "none"}}
              ref={fileInputRef}
            />
            <IconButton
              className={classes.attachIconBtn}
              onClick={() => fileInputRef?.current?.click()}
            >
              <AttachFileIcon className={classes.attachIcon} />
            </IconButton>
          </Box>
        </FlexBox>
        {userRole === "organiser" && (
          <Button
            className={classes.send}
            variant="contained"
            color="primary"
            endIcon={<SendIcon />}
            onClick={handleSend}
            disabled={sendDisabled}
          >
            Send
          </Button>
        )}
        {userRole !== "organiser" && (
          <Button
            className={classes.send}
            variant="contained"
            color="primary"
            onClick={handleSend}
            disabled={sendDisabled}
          >
            <SendIcon />
          </Button>
        )}
      </FlexBox>
      {sendError && (
        <Box color={theme.palette.error.main}>
          {t
            ? t(
                "exhibitor.chat.send_error",
                "Message was not sent, please check and try again"
              )
            : "Message was not sent, please check and try again"}
        </Box>
      )}
    </FlexBox>
  );
};

export default SendMessage;
